import React, { useState, useCallback } from 'react';
import { createRoot, Root } from 'react-dom/client';
import { GoalForm } from '../ui/diary/GoalForm';
import type {
  GoalFormData,
  GoalFormContainerProps,
} from '../shared/types/goals';

export type { GoalFormContainerProps };

const CREATE_GOAL_MUTATION = `
  mutation CreateGoal($input: CreateGoalInput!) {
    createGoal(input: $input) {
      goal {
        id
        fieldId
        targetCount
        period
      }
      errors
    }
  }
`;

const UPDATE_GOAL_MUTATION = `
  mutation UpdateGoal($input: UpdateGoalInput!) {
    updateGoal(input: $input) {
      goal {
        id
        targetCount
        period
      }
      errors
    }
  }
`;

const DELETE_GOAL_MUTATION = `
  mutation DeleteGoal($input: DeleteGoalInput!) {
    deleteGoal(input: $input) {
      success
      errors
    }
  }
`;

const CREATE_FIELD_SET_GOAL_MUTATION = `
  mutation CreateFieldSetGoal($input: CreateFieldSetGoalInput!) {
    createFieldSetGoal(input: $input) {
      goal {
        id
        fieldSetId
        targetCount
        period
        fieldIds
      }
      errors
    }
  }
`;

const UPDATE_FIELD_SET_GOAL_MUTATION = `
  mutation UpdateFieldSetGoal($input: UpdateFieldSetGoalInput!) {
    updateFieldSetGoal(input: $input) {
      goal {
        id
        targetCount
        period
        fieldIds
      }
      errors
    }
  }
`;

const DELETE_FIELD_SET_GOAL_MUTATION = `
  mutation DeleteFieldSetGoal($input: DeleteFieldSetGoalInput!) {
    deleteFieldSetGoal(input: $input) {
      success
      errors
    }
  }
`;

function GoalFormContainer({
  graphqlEndpoint,
  csrfToken,
  mode,
  goalType,
  fieldId,
  fieldSetId,
  fieldName,
  fieldSetName,
  initialData,
  goal,
  fields = [],
  backUrl,
  successUrl,
  labels,
}: GoalFormContainerProps) {
  const [errors, setErrors] = useState<Record<string, string[]>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const isFieldSetGoal = goalType === 'field_set';

  const formInitialData: GoalFormData = initialData || {
    targetCount: goal?.targetCount || 1,
    period: (goal?.period as GoalFormData['period']) || 'week',
    fieldIds: isFieldSetGoal && goal && 'fieldIds' in goal ? goal.fieldIds : [],
  };

  const handleSubmit = useCallback(
    async (data: GoalFormData) => {
      setIsSubmitting(true);
      setErrors({});

      let mutation: string;
      let variables: Record<string, unknown>;

      if (isFieldSetGoal) {
        mutation = mode === 'edit' ? UPDATE_FIELD_SET_GOAL_MUTATION : CREATE_FIELD_SET_GOAL_MUTATION;
        variables =
          mode === 'edit'
            ? {
                input: {
                  id: goal?.id,
                  targetCount: data.targetCount,
                  period: data.period,
                  fieldIds: data.fieldIds,
                },
              }
            : {
                input: {
                  fieldSetId,
                  targetCount: data.targetCount,
                  period: data.period,
                  fieldIds: data.fieldIds,
                },
              };
      } else {
        mutation = mode === 'edit' ? UPDATE_GOAL_MUTATION : CREATE_GOAL_MUTATION;
        variables =
          mode === 'edit'
            ? {
                input: {
                  id: goal?.id,
                  targetCount: data.targetCount,
                  period: data.period,
                },
              }
            : {
                input: {
                  fieldId,
                  targetCount: data.targetCount,
                  period: data.period,
                },
              };
      }

      try {
        const response = await fetch(graphqlEndpoint, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': csrfToken,
          },
          body: JSON.stringify({ query: mutation, variables }),
        });

        if (!response.ok) {
          throw new Error('Network error');
        }

        const result = await response.json();

        if (result.errors) {
          setErrors({ base: result.errors.map((e: { message: string }) => e.message) });
          setIsSubmitting(false);
          return;
        }

        const mutationKey = isFieldSetGoal
          ? mode === 'edit'
            ? 'updateFieldSetGoal'
            : 'createFieldSetGoal'
          : mode === 'edit'
          ? 'updateGoal'
          : 'createGoal';

        const mutationResult = result.data[mutationKey];

        if (mutationResult.errors && mutationResult.errors.length > 0) {
          setErrors({ base: mutationResult.errors });
          setIsSubmitting(false);
          return;
        }

        // Success - redirect
        window.location.href = successUrl;
      } catch {
        setErrors({ base: ['An unexpected error occurred. Please try again.'] });
        setIsSubmitting(false);
      }
    },
    [graphqlEndpoint, csrfToken, mode, goal, fieldId, fieldSetId, isFieldSetGoal, successUrl]
  );

  const handleDelete = useCallback(async () => {
    setIsDeleting(true);
    setErrors({});

    const mutation = isFieldSetGoal ? DELETE_FIELD_SET_GOAL_MUTATION : DELETE_GOAL_MUTATION;
    const mutationKey = isFieldSetGoal ? 'deleteFieldSetGoal' : 'deleteGoal';

    try {
      const response = await fetch(graphqlEndpoint, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        body: JSON.stringify({
          query: mutation,
          variables: { input: { id: goal?.id } },
        }),
      });

      if (!response.ok) {
        throw new Error('Network error');
      }

      const result = await response.json();

      if (result.errors) {
        setErrors({ base: result.errors.map((e: { message: string }) => e.message) });
        setIsDeleting(false);
        setShowDeleteConfirm(false);
        return;
      }

      const mutationResult = result.data[mutationKey];

      if (mutationResult.errors && mutationResult.errors.length > 0) {
        setErrors({ base: mutationResult.errors });
        setIsDeleting(false);
        setShowDeleteConfirm(false);
        return;
      }

      // Success - redirect
      window.location.href = backUrl;
    } catch {
      setErrors({ base: ['An unexpected error occurred. Please try again.'] });
      setIsDeleting(false);
    }
  }, [graphqlEndpoint, csrfToken, goal, isFieldSetGoal, backUrl]);

  const handleCancel = useCallback(() => {
    window.location.href = backUrl;
  }, [backUrl]);

  const handleDeleteClick = useCallback(() => {
    if (window.confirm(labels.deleteConfirm)) {
      handleDelete();
    }
  }, [handleDelete, labels.deleteConfirm]);

  const title = mode === 'edit' ? labels.editTitle : labels.title;
  const subtitle = isFieldSetGoal ? fieldSetName : fieldName;

  return (
    <div className="card">
      <div className="card-header">
        <h5 className="card-title mb-0">
          <i className="bi bi-bullseye me-2"></i>
          {title}
        </h5>
        {subtitle && <small className="text-muted">{subtitle}</small>}
      </div>
      <div className="card-body">
        <GoalForm
          initialData={formInitialData}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          onDelete={mode === 'edit' ? handleDeleteClick : undefined}
          labels={labels}
          mode={mode}
          isSubmitting={isSubmitting}
          isDeleting={isDeleting}
          errors={errors}
          isFieldSetGoal={isFieldSetGoal}
          availableFields={fields}
        />
      </div>
    </div>
  );
}

// Store root reference for cleanup
let formRoot: Root | null = null;

/**
 * Mount function for rendering from Rails
 */
export function mountGoalForm(container: HTMLElement, props: GoalFormContainerProps): Root {
  if (formRoot) {
    formRoot.unmount();
  }

  const root = createRoot(container);
  formRoot = root;
  root.render(<GoalFormContainer {...props} />);
  return root;
}

/**
 * Unmount function for cleanup
 */
export function unmountGoalForm(): void {
  if (formRoot) {
    formRoot.unmount();
    formRoot = null;
  }
}

export default GoalFormContainer;
